import { useEffect, useState } from "react";
import { api, formatCents } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Textarea } from "@/components/ui/Primitives";
import { Users2, Plus, Mail, Phone, Wrench } from "lucide-react";
import { toast } from "sonner";

const TRADES = [
  "plumber","electrician","hvac","cleaner","painter","locksmith",
  "general_contractor","carpenter","appliance_repair","landscaper",
  "pest_control","roofer","flooring","handyman"
];

export default function Vendors() {
  const [items, setItems] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", trade: "plumber", email: "", phone: "",
    hourly_rate: "", service_area: "", notes: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [v, i] = await Promise.all([
        api.get("/my-vendors"),
        api.get("/my-vendors/insights").catch(() => ({ data: null })),
      ]);
      setItems(Array.isArray(v.data) ? v.data : (v.data?.vendors || []));
      setInsights(i.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/my-vendors", {
        name: form.name.trim(),
        company: form.company || null,
        trade: form.trade,
        email: form.email || null,
        phone: form.phone || null,
        hourly_rate_cents: form.hourly_rate ? Math.round(Number(form.hourly_rate) * 100) : null,
        service_area: form.service_area || null,
        notes: form.notes || null,
      });
      toast.success("Vendor added");
      setOpen(false);
      setForm({ name: "", company: "", trade: "plumber", email: "", phone: "", hourly_rate: "", service_area: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add vendor");
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Vendors"
        subtitle="Your trusted network of trades and service providers."
        actions={<Button onClick={() => setOpen(true)} data-testid="add-vendor-button"><Plus className="w-4 h-4"/> Add vendor</Button>}
      />

      {insights && (
        <Card className="p-5 mb-6 bg-gradient-to-br from-slate-950 to-green-900 text-white border-0">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-200">Total vendors</div>
              <div className="font-display text-3xl font-bold mt-1">{insights.total || items.length}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-200">Avg hourly</div>
              <div className="font-display text-3xl font-bold mt-1">{insights.avg_hourly_cents ? formatCents(insights.avg_hourly_cents) : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-200">Top trade</div>
              <div className="font-display text-2xl font-bold mt-1 capitalize">{insights.top_trade?.replace(/_/g," ") || "—"}</div>
            </div>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={Users2}
          title="No vendors yet"
          description="Add trades and service providers you trust to assign work orders faster."
          action={null}
        />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((v) => (
            <Card key={v.vendor_id} className="p-5 card-lift" data-testid={`vendor-card-${v.vendor_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold text-slate-900 truncate">{v.name}</div>
                  {v.company && <div className="text-xs text-slate-500 truncate">{v.company}</div>}
                </div>
                <Pill tone="green" className="capitalize"><Wrench className="w-3 h-3"/> {v.trade?.replace(/_/g," ")}</Pill>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                {v.email && <a href={`mailto:${v.email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><Mail className="w-3.5 h-3.5"/> {v.email}</a>}
                {v.phone && <a href={`tel:${v.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><Phone className="w-3.5 h-3.5"/> {v.phone}</a>}
              </div>
              {v.hourly_rate_cents && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Hourly rate</span>
                  <span className="font-semibold text-slate-900">{formatCents(v.hourly_rate_cents)}/hr</span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add a vendor" testid="vendor-modal">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} testid="vendor-name-input"/>
          <Input label="Company (optional)" value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})}/>
          <Select label="Trade" value={form.trade} onChange={(e)=>setForm({...form, trade:e.target.value})} testid="vendor-trade-select">
            {TRADES.map((t) => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Email" type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}/>
            <Input label="Phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Hourly rate (CAD)" type="number" step="0.01" value={form.hourly_rate} onChange={(e)=>setForm({...form, hourly_rate:e.target.value})}/>
            <Input label="Service area" placeholder="GTA" value={form.service_area} onChange={(e)=>setForm({...form, service_area:e.target.value})}/>
          </div>
          <Textarea label="Notes" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})}/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="vendor-create-submit">{saving ? "Adding…" : "Add vendor"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
